import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

const STORAGE_KEY = 'trippin-theme';

/**
 * @typedef {'light' | 'dark' | 'system'} ThemeMode
 */

function getSystemTheme() {
  if (typeof window === 'undefined' || !window.matchMedia) return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function resolveTheme(mode) {
  return mode === 'system' ? getSystemTheme() : mode;
}

function applyResolvedTheme(resolvedTheme) {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  root.classList.toggle('dark', resolvedTheme === 'dark');

  // Helps built-in form controls match the active theme.
  try {
    root.style.colorScheme = resolvedTheme;
  } catch (_) {
    // ignore
  }
}

const ThemeContext = createContext(null);

export function ThemeProvider({ children }) {
  const [mode, setMode] = useState('system');
  const [resolvedTheme, setResolvedTheme] = useState(() => resolveTheme('system'));

  // Load persisted mode once.
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored === 'light' || stored === 'dark' || stored === 'system') {
        setMode(stored);
        setResolvedTheme(resolveTheme(stored));
      }
    } catch (_) {
      // ignore
    }
  }, []);

  // React to system theme changes (only in system mode).
  useEffect(() => {
    if (mode !== 'system') return;
    if (!window.matchMedia) return;

    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => setResolvedTheme(resolveTheme('system'));

    try {
      mql.addEventListener('change', onChange);
      return () => mql.removeEventListener('change', onChange);
    } catch (_) {
      // Safari < 14
      mql.addListener(onChange);
      return () => mql.removeListener(onChange);
    }
  }, [mode]);

  // Apply resolved theme to the document.
  useEffect(() => {
    const nextResolved = resolveTheme(mode);
    setResolvedTheme(nextResolved);
    applyResolvedTheme(nextResolved);

    try {
      localStorage.setItem(STORAGE_KEY, mode);
    } catch (_) {
      // ignore
    }
  }, [mode]);

  const setThemeMode = useCallback((nextMode) => {
    if (nextMode !== 'light' && nextMode !== 'dark' && nextMode !== 'system') return;
    setMode(nextMode);
  }, []);

  const value = useMemo(
    () => ({
      mode,
      resolvedTheme,
      setMode: setThemeMode,
    }),
    [mode, resolvedTheme, setThemeMode]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return ctx;
}

export const themeStorageKey = STORAGE_KEY;
